'use client'

import { useRef, useState, useEffect, useCallback, type ReactNode, type CSSProperties } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import Image from 'next/image'
import Link from 'next/link'
import { projects, slugify } from '../data/projects'
import { galleryStore } from '../store/galleryStore'
import { getLenis } from '../hooks/useSmoothScroll'
import { track } from '../lib/analytics'

gsap.registerPlugin(ScrollTrigger, useGSAP)

/**
 * SCENE 05: THE FORGE LINE (Selected Work)
 * A terminal index of nine projects on the left, an empty void stage in the
 * centre, and the active project's detail on the right. The WebGL particles
 * (HexField, phase 4) stand as a single tall hexagon on that empty centre stage
 * and RE-POUR — shatter downward and recast — in the project's own colour every
 * time you change selection. One luminous object, lots of black: the brand mark
 * forged anew per project, never fighting a grid. Desktop drives the particles;
 * phones get a clean list.
 */

const HEX_PTS = '50,3 93,27 93,73 50,97 7,73 7,27'

const accentOf = (p: (typeof projects)[number]) =>
  p.color && p.color !== '' && p.color !== '#000000' ? p.color : '#FF6B00'

// Project marks (by id); the rest fall back to a monoline category icon.
const LOGOS: Record<number, string> = {
  1: '/logos/noxiy.png',
  2: '/logos/loop-dimension.png',
  3: '/logos/opsera.png',
  7: '/logos/houzit.png',
  8: '/logos/makersgate.png',
}
const LOGO_WHITE: Record<number, boolean> = { 2: true } // Loop Dimension (black mark → white)

const ICONS: Record<number, ReactNode> = {
  4: (<><circle cx="5" cy="6.5" r="2" /><circle cx="19" cy="8" r="2" /><circle cx="12" cy="19" r="2" /><path d="M6.8 7.4 10.6 17M17.2 9.4 13.4 17M6.9 6.7 17 7.8" /></>), // Lunexa
  5: (<><path d="M3 17 9 11 13 14.5 21 6" /><path d="M21 6H16.5M21 6v4.5" /></>), // A-SIC
  6: (<><circle cx="12" cy="12" r="8.5" /><path d="M10 8 16 12 10 16Z" fill="currentColor" stroke="none" /></>), // Byond
  9: (<><path d="M12 4 22 9 12 14 2 9Z" /><path d="M6 11v4.6q6 3 12 0V11" /></>), // Unlimited EG
}

function Mark({ id, name, color, size }: { id: number; name: string; color: string; size: number }) {
  const logo = LOGOS[id]
  if (logo) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={logo} alt={`${name} logo`} width={size} height={size} draggable={false} style={{ height: size, width: 'auto', maxWidth: size * 1.4, objectFit: 'contain', filter: LOGO_WHITE[id] ? 'brightness(0) invert(1)' : 'none' }} />
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ height: size, width: 'auto' }}>
      {ICONS[id]}
    </svg>
  )
}

/* ─── Futuristic HUD chrome ─── */
const HEX_DOT = 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)'

// Four L-shaped corner brackets framing a relatively-positioned parent.
function HudCorners({ color, size = 14 }: { color: string; size?: number }) {
  const b: CSSProperties = { position: 'absolute', width: size, height: size, opacity: 0.55, transition: 'border-color .5s var(--ease-out-quart)' }
  return (
    <>
      <span aria-hidden style={{ ...b, top: 0, left: 0, borderTop: `1px solid ${color}`, borderLeft: `1px solid ${color}` }} />
      <span aria-hidden style={{ ...b, top: 0, right: 0, borderTop: `1px solid ${color}`, borderRight: `1px solid ${color}` }} />
      <span aria-hidden style={{ ...b, bottom: 0, left: 0, borderBottom: `1px solid ${color}`, borderLeft: `1px solid ${color}` }} />
      <span aria-hidden style={{ ...b, bottom: 0, right: 0, borderBottom: `1px solid ${color}`, borderRight: `1px solid ${color}` }} />
    </>
  )
}

// Rotating targeting reticle that frames the holographic hexagon.
function Reticle({ color }: { color: string }) {
  return (
    <svg aria-hidden viewBox="0 0 100 100" className="hud-reticle absolute pointer-events-none" style={{ width: 'min(40vh, 26rem)', aspectRatio: '1', left: '50%', top: '50%', transform: 'translate(-50%,-50%)' }}>
      <g className="hud-spin" style={{ transformOrigin: 'center', animation: 'hud-spin 30s linear infinite' }}>
        <circle cx="50" cy="50" r="48.5" fill="none" stroke={color} strokeWidth="0.35" strokeDasharray="0.4 3.6" opacity="0.5" />
        <line x1="50" y1="0.5" x2="50" y2="6" stroke={color} strokeWidth="0.7" opacity="0.8" />
        <line x1="50" y1="94" x2="50" y2="99.5" stroke={color} strokeWidth="0.7" opacity="0.8" />
        <line x1="0.5" y1="50" x2="6" y2="50" stroke={color} strokeWidth="0.7" opacity="0.8" />
        <line x1="94" y1="50" x2="99.5" y2="50" stroke={color} strokeWidth="0.7" opacity="0.8" />
      </g>
      <g className="hud-spin-rev" style={{ transformOrigin: 'center', animation: 'hud-spin-rev 22s linear infinite' }}>
        <circle cx="50" cy="50" r="41" fill="none" stroke={color} strokeWidth="0.5" strokeDasharray="16 84" opacity="0.65" />
        <circle cx="50" cy="50" r="41" fill="none" stroke={color} strokeWidth="0.5" strokeDasharray="16 84" strokeDashoffset="50" opacity="0.65" />
      </g>
    </svg>
  )
}

// Small hexagonal status indicator.
function HexDot({ color, on }: { color: string; on: boolean }) {
  return <span aria-hidden style={{ width: 9, height: 10, flexShrink: 0, clipPath: HEX_DOT, background: on ? color : 'var(--color-steel-deep)', boxShadow: on ? `0 0 9px ${color}` : 'none', transform: on ? 'scale(1)' : 'scale(0.78)', transition: 'all .4s var(--ease-out-quart)' }} />
}

export default function Gallery() {
  const sectionRef = useRef<HTMLElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)
  const [modal, setModal] = useState<number | null>(null)
  const [isDesktop, setIsDesktop] = useState(true)

  // Forge-Line refs: nav container + per-row buttons (ember launch points), the
  // rail's travelling lit node, the reticle bloom ring, the readout (ignition
  // target), and the in-flight ember timeline.
  const navRef = useRef<HTMLElement>(null)
  const navBtns = useRef<Array<HTMLButtonElement | null>>([])
  const railDotRef = useRef<HTMLSpanElement>(null)
  const stageBloomRef = useRef<HTMLDivElement>(null)
  const readoutRef = useRef<HTMLDivElement>(null)
  const flightTL = useRef<gsap.core.Timeline | null>(null)
  const activeRef = useRef(0)
  const isDesktopRef = useRef(true)

  useEffect(() => {
    const compute = () => {
      const d = window.innerWidth >= 1024
      isDesktopRef.current = d
      setIsDesktop(d)
    }
    compute()
    window.addEventListener('resize', compute)
    return () => window.removeEventListener('resize', compute)
  }, [])

  // Selecting a project fires a single ONE-SHOT BALLISTIC ember down the Forge
  // Line. The molten node arcs from the row's HexDot to the centre seal; at the
  // timeline midpoint the STRIKE causes the store re-pour (the WebGL shatter-and
  // -recast) + a bloom ring, then an ignition line races into the READOUT which
  // prints via the existing forge-wipe. Under reduced motion it is an instant
  // colour swap + a single soft bloom flash.
  const select = useCallback((i: number) => {
    if (i === activeRef.current) return
    activeRef.current = i
    galleryStore.index = i

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const color = accentOf(projects[i])
    const stage = stageRef.current
    const btn = navBtns.current[i]

    // The store re-pour (WebGL recast) + panel state — the causal payload.
    const commit = () => {
      setActive(i)
      galleryStore.active = i
      galleryStore.activeColor = color
    }

    // One-shot bloom ring on the reticle (GSAP centres via xPercent/yPercent so
    // scale composes cleanly). `soft` = the calm reduced-motion flash.
    const bloom = (soft: boolean) => {
      const el = stageBloomRef.current
      if (!el) return
      gsap.killTweensOf(el)
      gsap.set(el, { xPercent: -50, yPercent: -50, borderColor: color, boxShadow: `0 0 26px ${color}` })
      if (soft) gsap.fromTo(el, { opacity: 0, scale: 0.85 }, { opacity: 0.4, scale: 0.95, duration: 0.16, yoyo: true, repeat: 1, ease: 'sine.inOut' })
      else gsap.fromTo(el, { opacity: 0.85, scale: 0.32 }, { opacity: 0, scale: 1.45, duration: 0.55, ease: 'power2.out' })
    }

    // Tear down any ember still mid-flight from a previous selection.
    flightTL.current?.kill()
    flightTL.current = null
    document.querySelectorAll('[data-forge-fx]').forEach((n) => n.remove())

    // Reduced-motion / no-stage fallback: instant swap + a single soft bloom.
    if (reduce || !stage || !btn || !isDesktopRef.current) {
      commit()
      bloom(true)
      return
    }

    // Commit the colour + recast IMMEDIATELY on select — the seal must never lag
    // behind the active project (a late commit showed the previous project's colour
    // while scrolling). The ember, bloom and ignition line below are trailing flourish.
    commit()

    // Geometry: HexDot (row) → seal (stage centre). Reuse the stage rect the
    // publish loop already measures; the arc apex lifts above the line.
    const br = btn.getBoundingClientRect()
    const sr = stage.getBoundingClientRect()
    const sx = br.left + 5
    const sy = br.top + br.height / 2
    const tx = sr.left + sr.width / 2
    const ty = sr.top + sr.height / 2
    const cx = (sx + tx) / 2
    const cy = Math.min(sy, ty) - 90

    // Molten ember: wrapper carries the parabolic arc, child carries glow/scale
    // (kept on separate nodes so GSAP's transform never fights the arc write).
    const wrap = document.createElement('span')
    wrap.setAttribute('data-forge-fx', '')
    wrap.setAttribute('aria-hidden', 'true')
    Object.assign(wrap.style, { position: 'fixed', left: '0', top: '0', zIndex: '60', pointerEvents: 'none', willChange: 'transform' })
    const dot = document.createElement('span')
    Object.assign(dot.style, {
      position: 'absolute', left: '-7px', top: '-8px', width: '14px', height: '16px',
      clipPath: HEX_DOT, background: `radial-gradient(circle at 50% 40%, #fff, ${color} 62%)`,
      boxShadow: `0 0 14px ${color}, 0 0 30px ${color}`, transformOrigin: 'center',
    })
    wrap.appendChild(dot)
    document.body.appendChild(wrap)

    const proxy = { t: 0 }
    const setPos = (t: number) => {
      const u = 1 - t
      const x = u * u * sx + 2 * u * t * cx + t * t * tx
      const y = u * u * sy + 2 * u * t * cy + t * t * ty
      wrap.style.transform = `translate3d(${x}px, ${y}px, 0)`
    }
    setPos(0)

    const tl = gsap.timeline({ onComplete: () => { wrap.remove(); flightTL.current = null } })
    tl.fromTo(dot, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.12, ease: 'power2.out' }, 0)
    tl.to(proxy, { t: 1, duration: 0.42, ease: 'power2.in', onUpdate: () => setPos(proxy.t) }, 0)
    tl.to(dot, { scale: 1.4, duration: 0.12, ease: 'power1.in' }, 0.3)
    // IMPACT ≈ timeline midpoint: the strike lands with a bloom + ignition flourish
    // (the colour/recast already committed on select, so nothing lags).
    tl.add(() => {
      bloom(false)
      // Ignition line races from the seal into the READOUT (it then prints via forge-wipe).
      const rr = readoutRef.current?.getBoundingClientRect()
      const dist = rr ? Math.max(80, rr.left - tx + 20) : 220
      const line = document.createElement('span')
      line.setAttribute('data-forge-fx', '')
      line.setAttribute('aria-hidden', 'true')
      Object.assign(line.style, {
        position: 'fixed', left: `${tx}px`, top: `${ty}px`, height: '1px', width: '0px',
        transformOrigin: 'left center', background: `linear-gradient(90deg, ${color}, transparent)`,
        boxShadow: `0 0 6px ${color}`, pointerEvents: 'none', zIndex: '59',
      })
      document.body.appendChild(line)
      gsap.timeline({ onComplete: () => line.remove() })
        .to(line, { width: dist, duration: 0.22, ease: 'power2.out' })
        .to(line, { opacity: 0, duration: 0.2, ease: 'power1.in' }, '>-0.04')
    }, 0.42)
    tl.to(dot, { scale: 0, opacity: 0, duration: 0.16, ease: 'power2.in' }, 0.42)

    flightTL.current = tl
  }, [])

  // Clean up any in-flight ember / fx nodes on unmount.
  useEffect(() => () => {
    flightTL.current?.kill()
    document.querySelectorAll('[data-forge-fx]').forEach((n) => n.remove())
  }, [])

  // Slide the rail's lit node to the active row so the 'Forge Line' reads literal.
  useEffect(() => {
    if (!isDesktop) return
    const btn = navBtns.current[active]
    const mk = railDotRef.current
    if (!btn || !mk) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const y = btn.offsetTop + btn.offsetHeight / 2
    gsap.to(mk, { top: y, opacity: 1, duration: reduce ? 0 : 0.42, ease: 'power3.out' })
  }, [active, isDesktop])

  // Hover is DEBOUNCED (~140ms): sweeping the list with a trackpad no longer
  // machine-guns the expensive particle re-forge — only a settled hover selects.
  const hoverT = useRef<number | null>(null)
  const hoverSelect = useCallback((i: number) => {
    if (hoverT.current) window.clearTimeout(hoverT.current)
    hoverT.current = window.setTimeout(() => select(i), 140)
  }, [select])
  const cancelHover = useCallback(() => {
    if (hoverT.current) { window.clearTimeout(hoverT.current); hoverT.current = null }
  }, [])
  useEffect(() => () => { if (hoverT.current) window.clearTimeout(hoverT.current) }, [])

  // Seed the active project on mount so the hexagon arrives already coloured,
  // and make the colour sweep instant under reduced-motion.
  useEffect(() => {
    galleryStore.index = 0
    galleryStore.active = 0
    galleryStore.activeColor = accentOf(projects[0])
    galleryStore.sweepDur = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0.001 : 0.78
  }, [])

  // Publish the empty stage's screen-centre (viewport fraction, y-up) every frame
  // so the HexField hexagon stands exactly in the centre column.
  useEffect(() => {
    if (!isDesktop) return
    let raf = 0
    const update = () => {
      raf = 0
      const el = stageRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const cw = window.innerWidth, ch = window.innerHeight
      const cells = galleryStore.cells
      cells[0] = (r.left + r.width / 2) / cw - 0.5
      cells[1] = -((r.top + r.height / 2) / ch - 0.5)
      galleryStore.stageW = r.width / cw // size the hexagon to its column (stable on big screens)
    }
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update) }
    update()
    const settle = window.setTimeout(update, 300)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      cancelAnimationFrame(raf); clearTimeout(settle)
    }
  }, [isDesktop])

  // Reveal: the index rows and slab rise in as the section enters.
  useGSAP(
    () => {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (reduce) return
      gsap.fromTo('.forge-reveal',
        { y: 26, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, ease: 'power3.out', stagger: 0.05,
          scrollTrigger: { trigger: sectionRef.current, start: 'top 72%' } },
      )
    },
    { scope: sectionRef, dependencies: [isDesktop] },
  )

  const p = projects[active]
  const c = accentOf(p)

  return (
    <section
      ref={sectionRef}
      id="gallery"
      data-scene="gallery"
      className="relative w-full"
      style={{ backgroundColor: 'var(--color-void)', paddingTop: 'clamp(6rem, 9vh, 7rem)', paddingBottom: 0, minHeight: isDesktop ? '100vh' : undefined, display: isDesktop ? 'flex' : undefined, flexDirection: 'column' }}
    >
      {/* Section label — matches the other sections' top label, plus a futuristic status readout */}
      <div className="flex items-center justify-between gap-4" style={{ paddingInline: 'var(--gutter)', marginBottom: 'clamp(1.75rem, 4.5vh, 3rem)' }}>
        <div className="flex items-center gap-3">
          <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-fog)' }}>05</span>
          <svg width={10} height={10} viewBox="0 0 100 100" fill="none" style={{ flexShrink: 0 }}><polygon points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5" fill="none" stroke={c} strokeWidth="8" /></svg>
          <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-fog)' }}>FORGE.TERMINAL</span>
          <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-stone)' }}>/ selected work</span>
        </div>
        <div className="hidden lg:flex items-center gap-3 font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-stone)' }}>
          <span>{String(projects.length).padStart(2, '0')} UNITS</span>
          <span aria-hidden style={{ width: 1, height: 10, background: 'var(--color-border)' }} />
          <span className="flex items-center gap-1.5"><span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: c, boxShadow: `0 0 8px ${c}`, animation: 'dot-pulse 2.4s ease-in-out infinite' }} />ONLINE</span>
        </div>
      </div>

      {isDesktop ? (
        <div
          className="grid items-stretch"
          style={{ gridTemplateColumns: '0.85fr 1.25fr 1.25fr', gap: 'clamp(1.75rem, 3.5vw, 4.5rem)', paddingInline: 'var(--gutter)', flex: '1 1 auto', minHeight: '60vh' }}
        >
          {/* ── SELECTOR: terminal unit list ── */}
          <nav ref={navRef} className="relative flex flex-col justify-center" aria-label="Projects">
            <div className="font-mono uppercase" style={{ position: 'absolute', top: 0, left: 0, fontSize: 'var(--text-caption)', letterSpacing: '0.24em', color: 'var(--color-text-tertiary)' }}>SELECT_UNIT //</div>
            {/* The Forge Line: a 1px rail behind the HexDots with the active node lit on it. */}
            <span aria-hidden style={{ position: 'absolute', left: 4.5, top: '9%', bottom: '9%', width: 1, background: 'linear-gradient(180deg, transparent, var(--color-border) 12%, var(--color-border) 88%, transparent)' }} />
            <span ref={railDotRef} aria-hidden style={{ position: 'absolute', left: 4.5, top: 0, transform: 'translate(-50%, -50%)', width: 2, height: 18, borderRadius: 1, background: c, boxShadow: `0 0 10px ${c}`, opacity: 0, pointerEvents: 'none' }} />
            {projects.map((proj, i) => {
              const ac = accentOf(proj)
              const on = i === active
              return (
                <button
                  key={proj.id}
                  ref={(el) => { navBtns.current[i] = el }}
                  className="forge-reveal group relative flex items-center gap-3 w-full text-left"
                  style={{ height: 'clamp(2.6rem, 4.8vh, 3.4rem)', opacity: on ? 1 : 0.72, transition: 'opacity .45s var(--ease-out-quart)' }}
                  onMouseEnter={() => hoverSelect(i)}
                  onMouseLeave={cancelHover}
                  onFocus={() => { cancelHover(); select(i) }}
                  onClick={() => { cancelHover(); select(i); setModal(i) }}
                  aria-current={on}
                >
                  <HexDot color={ac} on={on} />
                  <span className="font-mono" style={{ fontSize: 11, letterSpacing: '0.14em', color: on ? ac : 'var(--color-text-tertiary)', transition: 'color .4s' }}>{String(i + 1).padStart(2, '0')}</span>
                  <span className="font-display font-bold uppercase leading-[0.98]" style={{ fontSize: on ? 'clamp(1.25rem,1.8vw,1.8rem)' : 'clamp(1rem,1.3vw,1.25rem)', letterSpacing: '-0.02em', color: on ? 'var(--color-text-primary)' : 'var(--color-text-secondary)', transition: 'font-size .45s var(--ease-out-quart), color .4s' }}>{proj.name}</span>
                  {on && <span aria-hidden className="font-mono" style={{ marginLeft: 'auto', color: ac, fontSize: 12, animation: 'blink 1.1s steps(2,start) infinite' }}>◂</span>}
                </button>
              )
            })}
          </nav>

          {/* ── PROJECTION: the particle hexagon as a holographic display ── */}
          <div ref={stageRef} className="relative">
            <HudCorners color={c} />
            <Reticle color={c} />
            {/* One-shot bloom ring — ignites on the ember's strike (GSAP-driven). */}
            <div ref={stageBloomRef} aria-hidden className="absolute pointer-events-none" style={{ left: '50%', top: '50%', width: 'min(40vh, 26rem)', aspectRatio: '1', borderRadius: '50%', border: `1px solid ${c}`, opacity: 0 }} />
            {/* faint giant project number — the forge seal's core */}
            <div key={active} className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ animation: 'kiln-fade .6s var(--ease-out-quint) both' }}>
              <span className="font-display font-bold tabular-nums" style={{ fontSize: 'clamp(5rem, 9.5vw, 9.5rem)', lineHeight: 1, letterSpacing: '-0.04em', color: c, opacity: 0.07 }}>{String(active + 1).padStart(2, '0')}</span>
            </div>
            {/* sweeping scan line */}
            <div aria-hidden className="hud-scan" style={{ position: 'absolute', left: '9%', right: '9%', top: 0, height: 1, background: `linear-gradient(90deg, transparent, ${c}, transparent)`, animation: 'hud-scan 6s var(--ease-in-out-quart) infinite' }} />
            {/* top + bottom HUD labels */}
            <div className="absolute font-mono uppercase flex items-center gap-2" style={{ top: 0, left: '50%', transform: 'translateX(-50%)', fontSize: 'var(--text-caption)', letterSpacing: '0.26em', color: 'var(--color-text-tertiary)' }}><span style={{ color: c }}>◇</span> PROJECTION</div>
            <div className="absolute inset-x-0 flex items-center justify-center gap-3 font-mono uppercase" style={{ bottom: 0, fontSize: 'var(--text-caption)', letterSpacing: '0.26em', color: 'var(--color-text-tertiary)' }}>
              <span style={{ color: c }}>{String(active + 1).padStart(2, '0')}</span>
              <span aria-hidden style={{ width: 30, height: 1, background: `linear-gradient(90deg,transparent,${c},transparent)` }} />
              <span>{String(projects.length).padStart(2, '0')}</span>
            </div>
          </div>

          {/* ── READOUT: the active project as a data panel ── */}
          <div ref={readoutRef} className="forge-reveal relative flex flex-col justify-center" style={{ minWidth: 0 }}>
            <div className="font-mono uppercase" style={{ position: 'absolute', top: 0, right: 0, fontSize: 'var(--text-caption)', letterSpacing: '0.24em', color: 'var(--color-text-tertiary)' }}>// READOUT</div>
            <div key={active}>
              <span className="block font-mono uppercase" style={{ fontSize: 'var(--text-mono-label)', letterSpacing: '0.24em', color: c, animation: 'forge-rise .5s var(--ease-out-quint) both' }}>UNIT {String(active + 1).padStart(2, '0')} — {p.category}</span>
              <h3 className="font-display font-bold uppercase" style={{ fontSize: 'clamp(2.1rem, 3.8vw, 4rem)', lineHeight: 0.96, letterSpacing: '-0.03em', color: 'var(--color-text-primary)', marginTop: 10, animation: 'forge-wipe .6s var(--ease-out-quint) both' }}>{p.name}</h3>

              <div style={{ animation: 'forge-rise .6s var(--ease-out-quint) .09s both' }}>
                {/* HUD field row */}
                <div className="flex flex-wrap gap-x-7 gap-y-2 font-mono uppercase" style={{ marginTop: 16, fontSize: 'var(--text-caption)', letterSpacing: '0.16em' }}>
                  <span><span style={{ color: 'var(--color-text-tertiary)' }}>CLASS / </span><span style={{ color: 'var(--color-text-secondary)' }}>{p.category}</span></span>
                  <span><span style={{ color: 'var(--color-text-tertiary)' }}>STATUS / </span><span style={{ color: p.url !== '#' ? c : 'var(--color-text-tertiary)' }}>{p.url !== '#' ? 'LIVE' : 'ARCHIVED'}</span></span>
                </div>

                {/* transmission */}
                <span className="block font-mono uppercase" style={{ fontSize: 'var(--text-caption)', letterSpacing: '0.24em', color: 'var(--color-text-tertiary)', marginTop: 18, marginBottom: 7 }}>// TRANSMISSION</span>
                <p className="font-body" style={{ fontSize: 'var(--text-body)', lineHeight: 1.6, color: 'var(--color-text-secondary)', maxWidth: '42ch' }}>{p.description}</p>

                {/* feed (screenshot) → opens full panel */}
                <button
                  onClick={() => setModal(active)}
                  className="relative block overflow-hidden"
                  style={{ width: '100%', maxWidth: '38rem', aspectRatio: '16 / 10', marginTop: 22, boxShadow: `0 26px 64px -34px ${c}` }}
                  aria-label={`Open ${p.name}`}
                >
                  <Image src={p.image} alt={p.alt} fill sizes="480px" className="object-cover" style={{ opacity: 0.9 }} />
                  <HudCorners color={c} size={10} />
                  <span className="absolute inset-x-0 bottom-0 flex items-center justify-between font-mono uppercase" style={{ padding: '0.5rem 0.65rem', fontSize: 9, letterSpacing: '0.18em', color: 'var(--color-text-primary)', background: 'linear-gradient(0deg, rgba(5,5,5,0.78), transparent 60%)' }}>
                    <span style={{ color: c }}>▸ FEED.{String(active + 1).padStart(2, '0')}</span>
                    <span>EXPAND ↗</span>
                  </span>
                </button>

                <div className="flex items-center gap-4" style={{ marginTop: 20 }}>
                  {p.url !== '#' && (
                    <a href={p.url} target="_blank" rel="noopener noreferrer" onClick={() => track('project_visit', { project: p.name })} className="font-mono uppercase whitespace-nowrap" style={{ fontSize: '0.72rem', letterSpacing: '0.18em', color: 'var(--color-on-fire)', background: c, padding: '0.65rem 1.2rem' }}>[ VISIT ↗ ]</a>
                  )}
                  <Link href={`/projects/${slugify(p.name)}`} className="font-mono uppercase whitespace-nowrap" style={{ fontSize: '0.72rem', letterSpacing: '0.18em', color: c, border: `1px solid ${c}66`, padding: '0.65rem 1.2rem', textDecoration: 'none' }}>[ DOSSIER ▸ ]</Link>
                  <Mark id={p.id} name={p.name} color={c} size={20} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* ── Mobile: clean list (no particles on phones) ── */
        <div className="flex flex-col gap-3" style={{ paddingInline: 'var(--gutter)' }}>
          {projects.map((proj, i) => {
            const ac = accentOf(proj)
            return (
              <button
                key={proj.id}
                onClick={() => setModal(i)}
                className="forge-reveal flex items-center gap-4 w-full text-left"
                style={{ padding: '0.9rem 1rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-surface)' }}
                aria-label={`Open ${proj.name}`}
              >
                <span className="font-mono" style={{ fontSize: 12, letterSpacing: '0.16em', color: ac, width: 24, flexShrink: 0 }}>{String(i + 1).padStart(2, '0')}</span>
                <span className="relative flex items-center justify-center" style={{ width: 44, height: 44, flexShrink: 0 }}>
                  <svg viewBox="0 0 100 100" fill="none" className="absolute inset-0 w-full h-full"><polygon points={HEX_PTS} fill="none" stroke={`${ac}66`} strokeWidth={3} /></svg>
                  <Mark id={proj.id} name={proj.name} color={ac} size={20} />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span className="block font-display font-bold uppercase leading-tight" style={{ fontSize: '1rem', letterSpacing: '-0.01em', color: 'var(--color-text-primary)' }}>{proj.name}</span>
                  <span className="block font-mono uppercase" style={{ fontSize: 9, letterSpacing: '0.16em', color: 'var(--color-text-tertiary)', marginTop: 2 }}>{proj.category}</span>
                </span>
                <span className="font-mono" style={{ marginLeft: 'auto', color: ac, fontSize: 13 }}>↗</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Scene divider */}
      <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,107,0,0.4), transparent)', marginInline: 'var(--gutter)', marginTop: 'clamp(3rem, 7vh, 5rem)' }} />

      {modal !== null && <DetailPanel project={projects[modal]} index={modal} onClose={() => setModal(null)} />}
    </section>
  )
}

/* ─── Detail panel — full screenshot, forged open ─── */
function DetailPanel({ project, index, onClose }: { project: (typeof projects)[number]; index: number; onClose: () => void }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const c = accentOf(project)

  useEffect(() => {
    const lenis = getLenis()
    lenis?.stop()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('keydown', onKey); lenis?.start() }
  }, [onClose])

  useGSAP(() => {
    gsap.fromTo(wrapRef.current, { opacity: 0 }, { opacity: 1, duration: 0.28, ease: 'power2.out' })
    gsap.fromTo('.detail-panel', { opacity: 0, scale: 0.94, y: 24 }, { opacity: 1, scale: 1, y: 0, duration: 0.55, ease: 'expo.out', delay: 0.04 })
  }, { scope: wrapRef })

  return (
    <div
      ref={wrapRef}
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 200, background: 'rgba(5,5,5,0.8)', backdropFilter: 'blur(8px)', padding: 'clamp(1rem, 4vw, 3rem)' }}
      onClick={onClose}
    >
      <div className="detail-panel relative w-full" style={{ maxWidth: '56rem' }} onClick={(e) => e.stopPropagation()}>
        <div className="relative w-full overflow-hidden" style={{ aspectRatio: '16 / 9', borderRadius: 'var(--radius-lg)', border: `1px solid ${c}`, boxShadow: `0 0 40px -8px ${c}` }}>
          <Image src={project.image} alt={project.alt} fill sizes="900px" className="object-cover" />
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4" style={{ marginTop: '1.5rem' }}>
          <div style={{ minWidth: 0 }}>
            <span className="font-mono uppercase" style={{ fontSize: 'var(--text-caption)', letterSpacing: '0.22em', color: c }}>{String(index + 1).padStart(2, '0')} · {project.category}</span>
            <h3 className="font-display font-bold uppercase" style={{ fontSize: 'var(--text-h3)', letterSpacing: '-0.02em', color: 'var(--color-text-primary)', marginTop: 6 }}>{project.name}</h3>
            <p className="font-body" style={{ fontSize: 'var(--text-body)', lineHeight: 1.6, color: 'var(--color-text-secondary)', maxWidth: '46ch', marginTop: 8 }}>{project.description}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Link href={`/projects/${slugify(project.name)}`} className="font-mono uppercase whitespace-nowrap" style={{ fontSize: '0.72rem', letterSpacing: '0.18em', color: c, border: `1px solid ${c}66`, padding: '0.7rem 1.2rem', borderRadius: 'var(--radius-sm)', textDecoration: 'none' }}>
              Dossier ▸
            </Link>
            {project.url !== '#' && (
              <a href={project.url} target="_blank" rel="noopener noreferrer" onClick={() => track('project_visit', { project: project.name })} className="font-mono uppercase whitespace-nowrap" style={{ fontSize: '0.72rem', letterSpacing: '0.18em', color: 'var(--color-on-fire)', background: c, padding: '0.7rem 1.2rem', borderRadius: 'var(--radius-sm)' }}>
                Visit ↗
              </a>
            )}
          </div>
        </div>

        <button onClick={onClose} aria-label="Close" className="absolute flex items-center justify-center font-mono" style={{ top: -14, right: -14, width: 36, height: 36, borderRadius: 'var(--radius-circle)', background: 'var(--color-surface-raised)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
          ✕
        </button>
      </div>
    </div>
  )
}
